import { useTranslation } from 'react-i18next';
import { Languages, Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import { LOCALE_NATIVE_NAMES, SUPPORTED_LOCALES, isLocale, type Locale } from '@shared/lib/i18n/locales';
import { usePreferences, type ThemePreference } from '@shared/model/preferences';
import { useLocale } from '@shared/hooks/useLocale';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { Menu, MenuContent, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuTrigger } from './Menu';

export interface LanguageSwitcherProps {
  onChange: (locale: Locale) => void;
  /** Show the current language name next to the icon (auth pages). */
  showLabel?: boolean;
}

/** Language names in their own script, never flags (i18n doc §13.2). */
export function LanguageSwitcher({ onChange, showLabel }: LanguageSwitcherProps) {
  const { t } = useTranslation('common');
  const locale = useLocale();
  return (
    <Menu>
      <MenuTrigger asChild>
        {showLabel ? (
          <button
            type="button"
            className="inline-flex min-h-touch-min items-center gap-2 rounded-md px-3 text-sm text-secondary hover:bg-surface-tint"
            aria-label={t('language.switch')}
          >
            <Icon icon={Languages} size={18} />
            <span lang={locale}>{LOCALE_NATIVE_NAMES[locale]}</span>
          </button>
        ) : (
          <IconButton icon={Languages} label={t('language.switch')} showTooltip={false} />
        )}
      </MenuTrigger>
      <MenuContent label={t('language.switch')}>
        <MenuLabel>{t('language.label')}</MenuLabel>
        <MenuRadioGroup
          value={locale}
          onValueChange={(value) => {
            if (isLocale(value) && value !== locale) onChange(value);
          }}
        >
          {SUPPORTED_LOCALES.map((code) => (
            <MenuRadioItem key={code} value={code} lang={code}>
              {LOCALE_NATIVE_NAMES[code]}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}

const THEME_ICONS: Record<ThemePreference, LucideIcon> = { light: Sun, dark: Moon, system: Monitor };

export function ThemeToggle() {
  const { t } = useTranslation('common');
  const theme = usePreferences((s) => s.theme);
  const setTheme = usePreferences((s) => s.setTheme);
  return (
    <Menu>
      <MenuTrigger asChild>
        <IconButton icon={THEME_ICONS[theme]} label={t('theme.switch')} showTooltip={false} />
      </MenuTrigger>
      <MenuContent label={t('theme.switch')}>
        <MenuLabel>{t('theme.label')}</MenuLabel>
        <MenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            if (value === 'light' || value === 'dark' || value === 'system') setTheme(value);
          }}
        >
          <MenuRadioItem value="light">{t('theme.light')}</MenuRadioItem>
          <MenuRadioItem value="dark">{t('theme.dark')}</MenuRadioItem>
          <MenuRadioItem value="system">{t('theme.system')}</MenuRadioItem>
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}
